/* eslint-disable max-depth */
import {
  EventEmitParams,
  EventErrorParams,
  EventLogParams,
  TypeStorageItems,
  ZTIRegion
} from '@byted/tiktok-zti-types';
import { WebCrossOriginStorage, WebStorage } from '@byted/tiktok-zti-storage-web';
import { CallEvent } from '@byted/tiktok-zti-call';
import { CrossLocalStorageConfig } from '../types';
import { compareAllKeysInfoMD5 } from '../utils';
import { getCookie } from '../utils/cookie';

function getRegion(region?: ZTIRegion) {
  if (region === 'eu') {
    return `https://sf16-website.neutral.tiktokcdn-eu.com/obj/tiktok_web_static_eu/tiktok/account/zti/html/main/index.html`;
  } else if (region === 'sg') {
    return `https://sf16-website.neutral.ttwstatic.com/obj/tiktok_web_static/tiktok/account/zti/html/main/index.html`;
  } else if (region === 'va') {
    return `https://sf16-website.neutral.ttwstatic.com/obj/tiktok_web_static/tiktok/account/zti/html/main/index.html`;
  } else if (region === 'oci') {
    return `https://lf16-ucenter.tiktokcdn-us.com/obj/ucenter-web-tt-tx/tiktok/account/zti/html/main/index.html`;
  } else {
    return `https://sf16-website.neutral.ttwstatic.com/obj/tiktok_web_static/tiktok/account/zti/html/main/index.html`;
  }
}

class CrossLocalStorage extends CallEvent<{
  load: EventEmitParams;
  execute: EventEmitParams;
  error: EventErrorParams;
  log: EventLogParams;
}> {
  storage?: WebCrossOriginStorage<{ [key: string]: string }>;
  localStore?: WebStorage<{ [key: string]: string }>;
  config: CrossLocalStorageConfig;
  disableCrossStorage?: boolean;
  hasCheckIframeStatus?: boolean;
  iframeStatus?: boolean;
  crossStatus?: boolean;
  loadIframePromise?: Promise<any>;

  constructor(props: CrossLocalStorageConfig) {
    super();
    const { url, fallbackCacheOriginURL, sendEvent, disableCrossStorage } = props;
    this.disableCrossStorage = disableCrossStorage;
    this.config = props;

    if (!this.disableCrossStorage) {
      const defaultURL = getRegion(props.region);
      this.localStore = new WebStorage();
      this.storage = new WebCrossOriginStorage({
        // url: url || '',
        url: url || defaultURL || '',
        protocol: 'SERCURE',
        fallback: fallbackCacheOriginURL,
        verifySignMethod: this.verifySignMethod
      });
      this.storage
        .setOriginStorageConfig({
          enableHotPatch: this.enableHotPath(),
          hostname: this.getCurrentHostName()
        })
        .catch((err) => {
          this.emit('error', { name: 'enbale hot patch error', error: err });
        });
      this.hasCheckIframeStatus = false;
      // 存储过程中是否使用iframe
      this.iframeStatus = false;

      // 是否初始化成功
      this.crossStatus = false;

      this.storage.on('error', (err) => {
        this.emit('error', { error: err, name: 'storage error' });
      });
      this.storage.on('log', (info) => {
        const { name, content } = info || {};
        this.emit('log', {
          extra: {
            content: content || ''
          },
          content: name,
          level: 'info'
        });
      });

      // delete 2023年07月19日16:18:04
      // this.storage.client.on('connectionFail', err => {
      // this.emit('load', {
      //   action: 'iframe',
      //   op: 'init',
      //   status: 'fail',
      // });
      // });

      this.storage.on('metrics', (args) => {
        const { name, metrics, categories } = args || {};
        if (name && typeof name === 'string') {
          sendEvent?.({ name: `storage_${name}`, metrics, categories });
        } else {
          sendEvent?.({ name: `storage_event_without_name`, metrics, categories });
        }
      });

      this.loadIframePromise = this.initLoadIframePromise();
    } else {
      this.localStore = new WebStorage();
      this.localStore.on('log', (info) => {
        const { name, content } = info || {};
        this.emit('log', {
          extra: {
            content: content || ''
          },
          content: name,
          level: 'info'
        });
      });
      this.localStore.on('error', (err) => {
        this.emit('error', { error: err, name: 'storage error' });
      });
    }
    // this.initUnloadEvent();
  }

  getCurrentHostName = () => {
    const hostname = window?.location?.hostname;
    // TODO: 先去除关键字，别用 creator.douy.com
    if (hostname && hostname.indexOf('second.tiktok.com') !== -1) {
      return hostname;
    }
    // if (hostname && hostname.indexOf('creator.douy.com') !== -1) {
    //   return hostname;
    // }
    return undefined;
  };

  startStorageChecker = () => {
    this.storage?.startStorageChecker().catch((err) => {
      this.emit('error', { name: 'start storage checker error', error: err });
    });
  };

  initLoadIframePromise = () =>
    new Promise<void>((resolve, reject) => {
      if (this.storage) {
        this.storage?.client.on('connection', (res: any) => {
          // { isReConnect, reConnectionCount, startTime, endTime }
          this.crossStatus = true;
          resolve();
        });
      } else {
        resolve();
      }
    });

  // 内部获取状态
  getStorageStatus = () => this.storage?.client.getIframeState();

  /**
   * 创建当前存储key
   * @param key 当前存储key string
   * @returns string
   */
  _createStorageKey = (key: string): string => {
    const { namespace } = this.config;
    if (namespace) {
      return `security-sdk/${namespace}/${key}`;
    }
    return `security-sdk/${key}`;
  };

  _deleteStorageKey = (key: string) => key.replace(/security-sdk\//g, '');

  // _createCookieKey = (key: string) => {
  //   return `__security_${key.replace(/\//g, '_')}`;
  // };

  getItem = async (key: string) => {
    const warpKey = this._createStorageKey(key);
    // this.emit('execute', { action: 'storage', op: 'getItem', status: 'start', ctx: { key } });
    // const startTime = new Date().getTime();
    let res;
    if (!this.disableCrossStorage) {
      res = await this.storage?.getItem(warpKey);
    } else {
      res = await this.localStore?.getItem(warpKey);
    }
    // const endTime = new Date().getTime();
    if (res) {
      const { value, from, origin } = res || {};
      // const cross = origin && origin.indexOf('lf-zt.douy.com') !== -1 ? '1' : '0';
      // this.checkStorageStatus(false, origin);
      // this.emit('execute', {
      //   action: 'storage',
      //   op: 'getItem',
      //   status: value ? 'success' : 'success_null',
      //   duration: endTime > startTime ? endTime - startTime : 0,
      //   ctx: { key, from: typeof from === 'number' ? from.toString() : '-99', origin: origin ? origin : '', cross },
      // });
      return value || '';
    }
    // this.emit('execute', {
    //   action: 'storage',
    //   op: 'getItem',
    //   duration: endTime > startTime ? endTime - startTime : 0,
    //   ctx: { key },
    //   status: 'success_null',
    // });
    return '';
  };

  // 获取当前域名 localStorage & indexedDB 数据
  getLocalItem = async (key: string) => {
    const warpKey = this._createStorageKey(key);
    // this.emit('execute', { action: 'localstorage', op: 'getItem', status: 'start', ctx: { key } });
    const startTime = new Date().getTime();
    let res;
    if (this.disableCrossStorage) {
      res = await this.localStore?.getItem(warpKey);
    } else {
      res = await this.storage?.storage.getItem(warpKey);
    }
    const endTime = new Date().getTime();
    if (res) {
      const { value, from, origin } = res || {};
      this.emit('execute', {
        action: 'localstorage',
        op: 'getItem',
        status: value ? 'success' : 'success_null',
        duration: endTime > startTime ? endTime - startTime : 0,
        ctx: {
          key,
          from: typeof from === 'number' ? from.toString() : '-99',
          origin: origin ? origin : '',
          type: 'localStorage'
        }
      });
      return value || '';
    }
    this.emit('execute', {
      action: 'localstorage',
      op: 'getItem',
      duration: endTime > startTime ? endTime - startTime : 0,
      ctx: { key, type: 'localStorage' },
      status: 'success_null'
    });
    return '';
  };

  // 设置当前域名 localStorage & indexedDB 数据
  setLocalItem = async (key: string, value: string) => {
    const warpKey = this._createStorageKey(key);
    // this.emit('execute', { action: 'localstorage', op: 'setItem', status: 'start', ctx: { key } });
    const startTime = new Date().getTime();
    let res;
    if (this.disableCrossStorage) {
      res = await this.localStore?.setItem(warpKey, value);
    } else {
      res = await this.storage?.storage.setItem(warpKey, value);
    }
    const { from, origin } = res || {};
    const endTime = new Date().getTime();
    // this.checkStorageStatus(false, origin);
    this.emit('execute', {
      action: 'localstorage',
      op: 'setItem',
      status: 'success',
      duration: endTime > startTime ? endTime - startTime : 0,
      ctx: { key, from: typeof from === 'number' ? from.toString() : '-99', origin: origin ? origin : '' }
    });
  };

  getLocalItemsWithKeys = async (keys: string[]) => {
    // this.emit('execute', { action: 'localstorage', op: 'getKeys', status: 'start', ctx: { keys: 'getKeys' } });
    const warpKeys = keys.map((ele) => this._createStorageKey(ele));
    const startTime = new Date().getTime();
    let values;
    if (this.disableCrossStorage) {
      values = await this.localStore?.getItemByKeys(warpKeys);
    } else {
      values = await this.storage?.storage?.getItemByKeys(warpKeys);
    }
    const endTime = new Date().getTime();
    const results: { [key: string]: any } = {};
    const ctx: { [key: string]: string } = {};
    // 全部从iframe获取 则cross为 1 反正为0
    if (values && Array.isArray(values) && values.length === keys.length) {
      values.forEach((res, index) => {
        const { value, from, origin } = res || {};
        const key = keys[index];
        const fromStr = typeof from === 'number' ? from.toString() : '-99';
        ctx[`${key.replace(/\//g, '_')}_origin`] = origin || '';
        ctx[`${key.replace(/\//g, '_')}_from`] = fromStr;
        ctx[`${key.replace(/\//g, '_')}_status`] = value ? 'success' : 'success_null';
        results[key] = value;
      });
      this.emit('execute', {
        action: 'localstorage',
        op: 'getKeys',
        status: 'success',
        duration: endTime > startTime ? endTime - startTime : 0,
        ctx
      });
      return results;
    }
    keys.forEach((element) => {
      results[element] = '';
      ctx[`${element.replace(/\//g, '_')}_status`] = 'success_null';
    });
    this.emit('execute', {
      action: 'localstorage',
      op: 'getKeys',
      duration: endTime > startTime ? endTime - startTime : 0,
      ctx,
      status: 'success_null'
    });
    return results;
  };

  getItemWithKeys = async (keys: string[]) => {
    // this.emit('execute', { action: 'storage', op: 'getKeys', status: 'start', ctx: { keys: 'getKeys' } });
    const warpKeys = keys.map((ele) => this._createStorageKey(ele));
    // const startTime = new Date().getTime();
    let values;
    if (this.disableCrossStorage) {
      values = await this.localStore?.getItemByKeys(warpKeys);
    } else {
      values = await this.storage?.getItemByKeys(warpKeys);
    }
    // const endTime = new Date().getTime();
    const results: { [key: string]: any } = {};
    const ctx: { [key: string]: string } = {};
    // 全部从iframe获取 则cross为 1 反正为0
    ctx.cross = '1';
    if (values && Array.isArray(values) && values.length === keys.length) {
      values.forEach((res, index) => {
        const { value, from, origin } = res || {};
        const key = keys[index];
        // TODO: 先去除 douy 关键字
        // if (origin && origin.indexOf('lf-zt.douy.com') === -1) {
        //   ctx.cross = '0';
        // }
        // this.checkStorageStatus(false, origin);
        const fromStr = typeof from === 'number' ? from.toString() : '-99';
        ctx[`${key.replace(/\//g, '_')}_origin`] = origin || '';
        ctx[`${key.replace(/\//g, '_')}_from`] = fromStr;
        ctx[`${key.replace(/\//g, '_')}_status`] = value ? 'success' : 'success_null';
        results[key] = value;
      });
      // this.emit('execute', {
      //   action: 'storage',
      //   op: 'getKeys',
      //   status: 'success',
      //   duration: endTime > startTime ? endTime - startTime : 0,
      //   ctx,
      // });
      return results;
    }
    keys.forEach((element) => {
      results[element] = '';
      ctx[`${element.replace(/\//g, '_')}_status`] = 'success_null';
    });
    // this.emit('execute', {
    //   action: 'storage',
    //   op: 'getKeys',
    //   duration: endTime > startTime ? endTime - startTime : 0,
    //   ctx,
    //   status: 'success_null',
    // });
    return results;
  };

  getItemsWithOrigin = async (
    keys: string[]
  ): Promise<{ data: { [key: string]: TypeStorageItems }; from?: string }> => {
    // this.emit('execute', { action: 'storage', op: 'getKeys', status: 'start', ctx: { keys: 'getKeys' } });
    const warpKeys = keys.map((ele) => this._createStorageKey(ele));
    // const startTime = new Date().getTime();
    let values;
    if (this.disableCrossStorage) {
      values = await this.localStore?.getItemByKeys(warpKeys);
    } else {
      values = await this.storage?.getItemByKeys(warpKeys);
    }
    // const endTime = new Date().getTime();
    // 删除节点判断逻辑
    // this.checkIframeStatus();
    const results: { data: { [key: string]: TypeStorageItems }; from?: string } = { data: {} };
    const data: { [key: string]: TypeStorageItems } = {};
    const ctx: { [key: string]: string } = {};
    ctx.cross = '1';
    if (values && Array.isArray(values) && values.length === keys.length) {
      values.forEach((res, index) => {
        const { value, from, origin } = res || {};
        const key = keys[index];
        // TODO: remove douy keyword
        // if (origin && origin.indexOf('lf-zt.douy.com') === -1) {
        //   ctx.cross = '0';
        // }
        // this.checkStorageStatus(false, origin);
        const fromStr = typeof from === 'number' ? from.toString() : '-99';
        ctx[`${key.replace(/\//g, '_')}_origin`] = origin || '';
        ctx[`${key.replace(/\//g, '_')}_from`] = fromStr;
        ctx[`${key.replace(/\//g, '_')}_status`] = value ? 'success' : 'success_null';
        // results[key] = value;
        data[key] = { key, value, from: fromStr || '-99', origin: origin || '-1' };
      });
      // this.emit('execute', {
      //   action: 'storage',
      //   op: 'getKeys',
      //   status: 'success',
      //   duration: endTime > startTime ? endTime - startTime : 0,
      //   ctx,
      // });
      results.data = data;
      results.from = ctx?.cross || '0';
      return results;
    }
    keys.forEach((element) => {
      data[element] = { key: element, value: '', from: '-98', origin: '-2' };
      ctx[`${element.replace(/\//g, '_')}_status`] = 'success_null';
    });
    // this.emit('execute', {
    //   action: 'storage',
    //   op: 'getKeys',
    //   duration: endTime > startTime ? endTime - startTime : 0,
    //   ctx,
    //   status: 'success_null',
    // });
    results.data = data;
    return results;
  };

  setItemWithKeys = async (keys: string[], values: string[], retryTime?: number) => {
    // this.emit('execute', { action: 'storage', op: 'setKeys', status: 'start', ctx: { keys: 'setKeys' } });
    if (keys.length !== values.length) {
      throw new Error('set item with Keys need equal length');
    }
    const items: [string, string][] = [];
    keys.forEach((element, index) => {
      const warpKey = this._createStorageKey(element);
      const item: [string, string] = [warpKey, values[index]];
      items.push(item);
    });
    // const startTime = new Date().getTime();
    let setResults;
    if (this.disableCrossStorage) {
      setResults = await this.localStore?.setItemByKeys(items);
    } else {
      setResults = await this.storage?.setItemByKeys(items, { async: 3000 });
    }
    // const endTime = new Date().getTime();
    const ctx: { [key: string]: string } = {};
    ctx.cross = '1';
    if (setResults && Array.isArray(setResults) && setResults.length === keys.length) {
      setResults.forEach((res, index) => {
        const { from, origin } = res || {};
        const key = keys[index];
        // TODO: remove douy keywords
        // if (origin && origin.indexOf('lf-zt.douy.com') === -1) {
        //   ctx.cross = '0';
        // }
        // this.checkStorageStatus(false, origin);
        const fromStr = typeof from === 'number' ? from.toString() : '-99';
        ctx[`${key.replace(/\//g, '_')}_origin`] = origin || '';
        ctx[`${key.replace(/\//g, '_')}_from`] = fromStr;
      });
      // this.emit('execute', {
      //   action: 'storage',
      //   op: 'setKeys',
      //   status: 'success',
      //   duration: endTime > startTime ? endTime - startTime : 0,
      //   ctx,
      // });
      return { cross: ctx.cross || '0' };
    }
    // this.emit('execute', {
    //   action: 'storage',
    //   op: 'setKeys',
    //   status: 'fail',
    //   duration: endTime > startTime ? endTime - startTime : 0,
    // });
    return { cross: '0' };
  };

  setItem = async (key: string, value: string, mode?: boolean) => {
    const warpKey = this._createStorageKey(key);
    // this.emit('execute', { action: 'storage', op: 'setItem', status: 'start', ctx: { key } });
    const startTime = new Date().getTime();
    let res;
    if (this.disableCrossStorage) {
      res = await this.localStore?.setItem(warpKey, value);
    } else {
      if (mode) {
        res = await this.storage?.setItem(warpKey, value, { async: mode });
      } else {
        res = await this.storage?.setItem(warpKey, value, { async: true });
      }
    }
    const { from, origin } = res || {};
    const endTime = new Date().getTime();
    const cross = '0';
    // TODO: remove douy keyword
    // const cross = origin && origin.indexOf('lf-zt.douy.com') !== -1 ? '1' : '0';
    // this.checkStorageStatus(false, origin);
    // this.emit('execute', {
    //   action: 'storage',
    //   op: 'setItem',
    //   status: 'success',
    //   duration: endTime > startTime ? endTime - startTime : 0,
    //   ctx: { key, from: typeof from === 'number' ? from.toString() : '-99', origin: origin ? origin : '', cross },
    // });
    return { cross };
  };

  deleteItem = async (key: string) => {
    const warpKey = this._createStorageKey(key);
    // this.emit('execute', { action: 'storage', op: 'deleteItem', status: 'start', ctx: { key } });
    if (this.disableCrossStorage) {
      await this.localStore?.removeItem(warpKey);
    } else {
      await this.storage?.removeItem(warpKey);
    }
  };

  checkIframeStatus = (unload?: boolean) => {
    try {
      if (this.hasCheckIframeStatus && !unload) {
        return;
      }
      this.hasCheckIframeStatus = true;
      const iframeList = document?.querySelectorAll?.('iframe');
      const loadSuccess = false;
      if (iframeList && iframeList.length > 0) {
        iframeList.forEach((iframe) => {
          const iframeUrl = iframe.src;
          // TODO: remove douy keyword
          // if (iframeUrl && iframeUrl.indexOf('lf-zt.douy.com') !== -1) {
          //   this.emit('execute', {
          //     action: 'iframe',
          //     op: unload ? 'check' : 'getKeys',
          //     status: 'success',
          //     ctx: { type: unload ? 'unload' : 'getKeys' }
          //   });
          //   loadSuccess = true;
          // }
        });
      }
      if (!loadSuccess) {
        this.emit('execute', {
          action: 'iframe',
          op: unload ? 'check' : 'getKeys',
          status: 'fail',
          ctx: { type: unload ? 'unload' : 'getKeys' }
        });
      }
    } catch (error) {
      this.emit('error', { error, name: 'check iframe status error' });
      this.emit('execute', {
        action: 'iframe',
        op: unload ? 'check' : 'getKeys',
        status: 'fail',
        ctx: { type: unload ? 'unload' : 'getKeys' }
      });
    }
  };

  // checkStorageStatus = (unload: boolean, origin?: string) => {
  //   try {
  //     if (this.iframeStatus) {
  //       return;
  //     } else if (unload) {
  //       this.emit('execute', {
  //         action: 'iframe',
  //         op: 'process',
  //         status: 'fail',
  //         ctx: { type: 'unload' },
  //       });
  //       return;
  //     } else if (origin && origin.indexOf('lf-zt.douy.com') !== -1) {
  //       this.iframeStatus = true;
  //       this.emit('execute', {
  //         action: 'iframe',
  //         op: 'process',
  //         status: 'success',
  //         ctx: { type: 'storage' },
  //       });
  //     }
  //   } catch (error) {
  //     this.emit('error', { error, name: 'check cross storage status error' });
  //   }
  // };

  getIframeStatus = () => this.crossStatus || this.iframeStatus;

  verifySignMethod = (data: string[]): boolean => {
    try {
      const COOKIE_CRYPT = '_tt_ticket_crypt_cookie';
      const cookieCrypt = getCookie(COOKIE_CRYPT) || '';
      if (data && Array.isArray(data) && data.length === 3) {
        const cryptData = data[0];
        const certData = data[1];
        const serverData = data[2];
        return compareAllKeysInfoMD5(cookieCrypt, cryptData, certData, serverData);
      }
    } catch (error) {
      this.emit('error', { error, name: 'verify sign method error' });
    }
    return false;
  };

  enableHotPath = (): boolean => {
    try {
      const host = document.location.href;
      // 先去除关键字，别用 creator.douy.com
      if (host.indexOf('second.tiktok.com') !== -1) {
        return true;
      }
    } catch (error) {
      this.emit('error', { error, name: 'enable hot path error' });
    }
    return false;
  };

  initUnloadEvent = () => {
    try {
      // const that = this;
      // const keys = [
      //   'security-sdk/s_sdk_crypt_sdk',
      //   'security-sdk/s_sdk_cert_key',
      //   'security-sdk/s_sdk_sign_data_key/web_protect',
      // ];
      window.addEventListener('beforeunload', (event) => {
        // 2023年07月19日 删除 unload getKeys埋点 上报
        // that.storage
        //   .getCenterOriginStorageStatusByKeys(keys)
        //   .then(status => {
        //     let ctx: { [key: string]: string } = {};
        //     keys.forEach(ele => {
        //       const { origin, from } = status?.[ele] || {};
        //       const fromStr = typeof from === 'number' ? from.toString() : '-99';
        //       const realKey = that._deleteStorageKey(ele);
        //       ctx[`${realKey.replace(/\//g, '_')}_origin`] = origin || '';
        //       ctx[`${realKey.replace(/\//g, '_')}_from`] = fromStr;
        //     });
        //     that.emit('execute', {
        //       action: 'unload',
        //       op: 'check',
        //       status: 'finish',
        //       ctx,
        //     });
        //   })
        //   .catch(err => {
        //     console.log(err);
        //   });
        // that.checkIframeStatus(true);
        // that.checkStorageStatus(true);
        // Cancel the event as stated by the standard.
        // event.preventDefault();
        // Chrome requires returnValue to be set.
        // event.returnValue = '';
      });
    } catch (error) {
      this.emit('error', { error, name: 'unload check error' });
    }
  };
}

export default CrossLocalStorage;
